import { BadgeCheck, Banknote, Briefcase, CalendarDays, Clock, GraduationCap, Heart, MapPin, MessageCircle, Navigation, Phone, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, Icon } from '@/components/ui';
import { openStatus } from '@/lib/hours';
import { closesText, jobTypeLabel, salaryText } from '@/lib/jobs';
import { useI18n } from '@/lib/i18n';
import { toggleSaved, useIsSaved } from '@/lib/saved';
import type { PublicItem } from '@/lib/types';
import { cn, directionsHref, formatDate, telHref, waHref } from '@/lib/utils';
import { SmartImage } from './SmartImage';

export const KIND_PATH: Record<string, string> = { business: 'businesses', place: 'places', temple: 'temples', village: 'villages', event: 'events', article: 'articles', history: 'history', gallery: 'gallery', job: 'jobs' };
export const itemPath = (item: Pick<PublicItem, 'kind' | 'slug'>) => `/${KIND_PATH[item.kind]}/${encodeURIComponent(item.slug)}`;
export const itemTitle = (item: PublicItem) => item.name ?? item.title;
export const itemImage = (item: PublicItem) => item.cover?.url ? item.cover : item.poster?.url ? item.poster : item.logo?.url ? item.logo : item.gallery?.[0] ?? item.images?.[0] ?? item.items?.[0]?.image;

export function SampleBadge({ show }: { show?: boolean }) {
  const { t } = useI18n();
  if (!show) return null;
  return <Badge tone="gold" className="backdrop-blur">{t('sample')}</Badge>;
}

export function VerifiedBadge({ item, className }: { item: PublicItem; className?: string }) {
  const { t } = useI18n();
  if (item.verification?.status !== 'verified') return null;
  return (
    <span title="Details confirmed by Mana Yelamanchili moderators. Not a government certification." className={cn('inline-flex items-center gap-1 rounded-full bg-forest-50 px-2 py-0.5 text-xs font-semibold text-forest-700 ring-1 ring-inset ring-forest-200', className)}>
      <BadgeCheck className="size-3.5" /> {t('verified')}
    </span>
  );
}

export function SaveButton({ item, className }: { item: PublicItem; className?: string }) {
  const saved = useIsSaved(item._id);
  const { L } = useI18n();
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSaved(item); }}
      aria-pressed={saved}
      aria-label={`${saved ? 'Remove from' : 'Save to'} saved places: ${L(itemTitle(item))}`}
      className={cn('relative z-10 flex size-9 items-center justify-center rounded-full bg-white/95 text-ink-muted shadow-sm ring-1 ring-black/5 transition hover:scale-105 hover:text-rose-500', saved && 'text-rose-500', className)}
    >
      <Heart className={cn('size-[18px]', saved && 'fill-current')} />
    </button>
  );
}

function OpenBadge({ item }: { item: PublicItem }) {
  const st = openStatus(item.hours);
  if (!st) return null;
  return <span className={cn('text-xs font-semibold', st.open ? 'text-forest-600' : 'text-rose-600')}>{st.label}</span>;
}

function subtitleOf(item: PublicItem, L: (v: any) => string, lang: 'en' | 'te') {
  switch (item.kind) {
    case 'business': return L(item.subcategory?.name) || L(item.category?.name);
    case 'temple': return L(item.deity);
    case 'event': return formatDate(item.startsAt, lang, { weekday: 'short', day: 'numeric', month: 'short', hour: item.allDay ? undefined : 'numeric', minute: item.allDay ? undefined : '2-digit' });
    case 'article': return formatDate(item.publishedAt, lang);
    case 'history': return item.period;
    case 'place': return item.placeType?.replace('_', ' ');
    case 'village': return item.isTown ? (lang === 'te' ? 'పట్టణం' : 'Town') : (lang === 'te' ? 'గ్రామం' : 'Village');
    default: return undefined;
  }
}

/** Text-first card for job openings: most postings have no photo, so the employer logo (or an icon) leads. */
export function JobCard({ item }: { item: PublicItem }) {
  const { L, lang } = useI18n();
  const title = L(itemTitle(item));
  const salary = salaryText(item.salary, lang);
  const place = item.village ? L(item.village.name) : L(item.address);
  const closes = closesText(item.closesAt, item.closed, lang);
  const soon = !item.closed && item.closesAt && new Date(item.closesAt).getTime() - Date.now() < 3 * 864e5;
  return (
    <article className="group relative flex h-full flex-col rounded-2xl border border-line bg-white p-4 shadow-card transition hover:-translate-y-0.5 hover:shadow-lift">
      <div className="flex items-start gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-brand-50 text-brand-600">
          {item.logo?.url ? <SmartImage image={item.logo} rounded={false} className="h-full w-full object-contain" sizes="48px" /> : <Briefcase className="size-6" />}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 font-semibold leading-snug text-ink">
            <Link to={itemPath(item)} className="after:absolute after:inset-0 focus:outline-none">{title}</Link>
          </h3>
          {item.company?.name && <p className="truncate text-sm text-ink-muted">{item.company.name}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <SampleBadge show={item.isSample} />
        {item.jobType && <Badge tone="teal">{jobTypeLabel(item.jobType, lang)}</Badge>}
        {item.closed && <Badge tone="neutral">{closes}</Badge>}
      </div>
      <ul className="mt-3 space-y-1.5 text-sm text-ink-soft">
        {salary && <li className="flex items-center gap-2"><Banknote className="size-4 shrink-0 text-forest-600" /><span className="font-medium text-ink">{salary}</span></li>}
        {place && <li className="flex items-center gap-2"><MapPin className="size-4 shrink-0 text-brand-500" /><span className="truncate">{place}</span></li>}
        {(item.experience || item.education) && <li className="flex items-center gap-2"><GraduationCap className="size-4 shrink-0 text-brand-500" /><span className="truncate">{[item.experience, item.education].filter(Boolean).join(' · ')}</span></li>}
      </ul>
      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-xs text-ink-muted">
        {!item.closed && closes && <span className={cn('inline-flex items-center gap-1', soon && 'font-semibold text-gold-800')}><Clock className="size-3.5" />{closes}</span>}
        {item.openings ? <span className="inline-flex items-center gap-1"><Users className="size-3.5" />{item.openings} {lang === 'te' ? 'ఖాళీలు' : item.openings === 1 ? 'opening' : 'openings'}</span> : null}
      </div>
    </article>
  );
}

/** Generic card for any public listing type (image card or compact list row). */
export function ListingCard({ item, layout = 'grid' }: { item: PublicItem; layout?: 'grid' | 'list' }) {
  if (item.kind === 'job') return <JobCard item={item} />;
  return <MediaCard item={item} layout={layout} />;
}

function MediaCard({ item, layout }: { item: PublicItem; layout: 'grid' | 'list' }) {
  const { L, t, lang } = useI18n();
  const title = L(itemTitle(item));
  const subtitle = subtitleOf(item, L, lang);
  const text = L(item.excerpt) || L(item.summary) || L(item.intro) || L(item.description);
  const place = item.village ? L(item.village.name) : item.address?.area;
  const canSave = ['business', 'place', 'temple', 'village', 'event'].includes(item.kind);

  if (layout === 'list') {
    return (
      <article className="group relative flex gap-4 rounded-2xl border border-line bg-white p-3 shadow-card transition hover:-translate-y-0.5 hover:shadow-lift">
        <div className="relative size-24 shrink-0 overflow-hidden rounded-xl bg-surface-sunken sm:size-28">
          <SmartImage image={itemImage(item)} rounded={false} className="h-full w-full transition duration-500 group-hover:scale-105" sizes="112px" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col py-0.5">
          <div className="flex items-start gap-2">
            <h3 className="min-w-0 flex-1 truncate font-semibold text-ink">
              <Link to={itemPath(item)} className="after:absolute after:inset-0 focus:outline-none">{title}</Link>
            </h3>
            {item.contact?.phone ? (
              <a href={telHref(item.contact.phone)} aria-label={`${t('call')} ${title}`} className="relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600 hover:bg-brand-600 hover:text-white"><Phone className="size-4" /></a>
            ) : null}
            {canSave && <SaveButton item={item} className="size-8 shadow-none ring-line" />}
          </div>
          {subtitle && <p className="truncate text-sm capitalize text-ink-muted">{subtitle}</p>}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
            <VerifiedBadge item={item} />
            {item.isSample && <span className="rounded bg-gold-50 px-1.5 py-0.5 text-[10px] font-semibold text-gold-800">{t('sample')}</span>}
          </div>
          {place && <p className="mt-auto flex items-center gap-1 pt-1 text-xs text-ink-muted"><MapPin className="size-3.5 text-brand-500" />{place}</p>}
          <OpenBadge item={item} />
        </div>
      </article>
    );
  }

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-card transition hover:-translate-y-1 hover:shadow-lift">
      <div className="relative aspect-[4/3] shrink-0 overflow-hidden bg-surface-sunken">
        <SmartImage image={itemImage(item)} rounded={false} className="h-full w-full transition duration-500 group-hover:scale-[1.04]" sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />
        <div className="absolute left-2.5 top-2.5 flex flex-wrap gap-1">
          <SampleBadge show={item.isSample} />
          {item.kind === 'event' && item.timing === 'ongoing' && <Badge tone="green">{t('ongoing')}</Badge>}
          {item.businessStatus === 'temporarily_closed' && <Badge tone="red">{t('temporarilyClosed')}</Badge>}
        </div>
        {canSave && <SaveButton item={item} className="absolute right-2.5 top-2.5" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col p-4">
        <h3 className="line-clamp-2 font-semibold leading-snug text-ink">
          <Link to={itemPath(item)} className="after:absolute after:inset-0 focus:outline-none">{title}</Link>
        </h3>
        {subtitle && (
          <p className="mt-0.5 flex items-center gap-1.5 text-sm capitalize text-ink-muted">
            {item.kind === 'business' && item.category?.icon && <Icon name={item.category.icon} className="size-3.5 text-brand-500" />}
            {item.kind === 'event' && <CalendarDays className="size-3.5 text-brand-500" />}
            <span className="truncate">{subtitle}</span>
          </p>
        )}
        {text && ['article', 'history', 'place'].includes(item.kind) && <p className="mt-1.5 line-clamp-2 text-sm text-ink-muted">{text.replace(/[#*_>`]/g, '')}</p>}
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-xs text-ink-muted">
          {place && <span className="inline-flex items-center gap-1"><MapPin className="size-3.5 text-brand-500" />{place}</span>}
          <VerifiedBadge item={item} />
          <OpenBadge item={item} />
        </div>
        {item.kind === 'business' && (item.contact?.phone || item.contact?.whatsapp || item.location) && (
          <div className="relative z-10 mt-3 flex gap-1 border-t border-line pt-3">
            {item.contact?.phone && <a href={telHref(item.contact.phone)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-50"><Phone className="size-3.5" />{t('call')}</a>}
            {item.contact?.whatsapp && <a href={waHref(item.contact.whatsapp)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-forest-600 hover:bg-forest-50"><MessageCircle className="size-3.5" />{t('whatsapp')}</a>}
            {item.location?.coordinates && <a href={directionsHref(item.location.coordinates)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-ink-soft hover:bg-surface-sunken"><Navigation className="size-3.5" />{t('directions')}</a>}
          </div>
        )}
      </div>
    </article>
  );
}

export function CardGridSkeleton({ count = 6, columns = 3 }: { count?: number; columns?: number }) {
  return (
    <div className={cn('grid gap-5 sm:grid-cols-2', columns === 4 ? 'lg:grid-cols-4' : columns === 2 ? '' : 'lg:grid-cols-3 xl:grid-cols-4')}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-2xl border border-line bg-white">
          <div className="skeleton aspect-[4/3]" />
          <div className="space-y-2 p-4">
            <div className="skeleton h-3 w-1/3 rounded" />
            <div className="skeleton h-4 w-3/4 rounded" />
            <div className="skeleton h-3 w-1/2 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function gridCols(columns?: number) {
  return columns === 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';
}
